/**
 * Findings panel (spec §2 item 3): severity-grouped validation findings, each
 * with a section-number citation chip (e.g. "NFPA 72 §17.6.3.1" — never code
 * prose), measured-vs-required values rendered inline (the Endra trust
 * pattern), and engineer override-with-reason (optimistic; overridden state
 * shows the reason and can be removed).
 */
import { useState } from "react";
import type { CSSProperties } from "react";
import type { ApiFinding, FindingCitation, FindingSeverity } from "../api/types";
import { FINDING_SEVERITIES } from "../api/types";

const SEVERITY_LABELS: Record<FindingSeverity, string> = {
  error: "Errors",
  warning: "Warnings",
  info: "Info",
};

const SEVERITY_COLORS: Record<FindingSeverity, string> = {
  error: "#e06c75",
  warning: "#e5c07b",
  info: "#61afef",
};

/** "NFPA 72 §17.6.3.1" — section numbers only. */
export function citationText(citation: FindingCitation): string {
  return `${citation.document} §${citation.section}`;
}

/** Findings grouped by severity in fixed order (error, warning, info). */
export function groupBySeverity(
  findings: readonly ApiFinding[],
): { severity: FindingSeverity; findings: ApiFinding[] }[] {
  return FINDING_SEVERITIES.map((severity) => ({
    severity,
    findings: findings.filter((f) => f.severity === severity),
  })).filter((g) => g.findings.length > 0);
}

const chipStyle: CSSProperties = {
  display: "inline-block",
  padding: "0.05rem 0.45rem",
  borderRadius: 999,
  fontSize: "0.72rem",
  background: "#2a2a44",
  color: "#8e8eb8",
  border: "1px solid #555577",
  whiteSpace: "nowrap",
};

function MeasuredVsRequired({ finding }: { finding: ApiFinding }) {
  if (finding.measured === null && finding.required === null) return null;
  return (
    <span data-testid="finding-values" style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
      {finding.measured !== null && (
        <span style={{ color: "#e06c75" }}>measured {finding.measured}</span>
      )}
      {finding.measured !== null && finding.required !== null && (
        <span style={{ color: "#8e8eb8" }}> vs </span>
      )}
      {finding.required !== null && (
        <span style={{ color: "#98c379" }}>required {finding.required}</span>
      )}
    </span>
  );
}

interface FindingRowProps {
  finding: ApiFinding;
  onOverride: (findingId: string, reason: string) => void;
  onClearOverride: (findingId: string) => void;
}

function FindingRow({ finding, onOverride, onClearOverride }: FindingRowProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <li
      data-testid="finding-row"
      data-finding-id={finding.id}
      style={{
        background: "#21213a",
        borderRadius: 8,
        padding: "0.6rem 0.75rem",
        marginBottom: "0.4rem",
        opacity: finding.overridden ? 0.65 : 1,
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        <span
          aria-hidden
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: SEVERITY_COLORS[finding.severity],
            flexShrink: 0,
          }}
        />
        <span
          style={{
            textDecoration: finding.overridden ? "line-through" : "none",
            flex: 1,
            minWidth: 200,
          }}
        >
          {finding.message}
        </span>
        {finding.citation && (
          <span data-testid="citation-chip" style={chipStyle}>
            {citationText(finding.citation)}
          </span>
        )}
        <MeasuredVsRequired finding={finding} />
      </div>

      {finding.overridden ? (
        <div
          data-testid="finding-overridden"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.82rem" }}
        >
          <span style={{ color: "#e5c07b" }}>
            Overridden{finding.override_reason ? `: ${finding.override_reason}` : ""}
          </span>
          <button
            type="button"
            data-testid="remove-override"
            onClick={() => onClearOverride(finding.id)}
            style={{
              border: "none",
              background: "transparent",
              color: "#4a90d9",
              cursor: "pointer",
              padding: 0,
              fontSize: "0.82rem",
            }}
          >
            Remove override
          </button>
        </div>
      ) : dialogOpen ? (
        <form
          data-testid="override-dialog"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length === 0) return;
            onOverride(finding.id, reason.trim());
            setDialogOpen(false);
            setReason("");
          }}
          style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}
        >
          <textarea
            data-testid="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded and shown in reports)"
            rows={2}
            style={{
              flex: 1,
              background: "#12121e",
              color: "#d8d8e8",
              border: "1px solid #555577",
              borderRadius: 6,
              padding: "0.35rem 0.5rem",
              resize: "vertical",
            }}
          />
          <button
            type="submit"
            data-testid="confirm-override"
            disabled={reason.trim().length === 0}
            style={{
              padding: "0.35rem 0.7rem",
              borderRadius: 6,
              border: "1px solid #4a90d9",
              background: reason.trim().length === 0 ? "#2a2a44" : "#4a90d9",
              color: "#fff",
              cursor: reason.trim().length === 0 ? "not-allowed" : "pointer",
            }}
          >
            Override
          </button>
          <button
            type="button"
            onClick={() => setDialogOpen(false)}
            style={{
              padding: "0.35rem 0.7rem",
              borderRadius: 6,
              border: "1px solid #555577",
              background: "transparent",
              color: "#8e8eb8",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div>
          <button
            type="button"
            data-testid="override-button"
            onClick={() => setDialogOpen(true)}
            style={{
              border: "none",
              background: "transparent",
              color: "#4a90d9",
              cursor: "pointer",
              padding: 0,
              fontSize: "0.82rem",
            }}
          >
            Override with reason…
          </button>
        </div>
      )}
    </li>
  );
}

export interface FindingsPanelProps {
  findings: ApiFinding[];
  onOverride: (findingId: string, reason: string) => void;
  onClearOverride: (findingId: string) => void;
  /** Surface the store's last override failure, if any. */
  overrideError?: string | null;
}

export function FindingsPanel({
  findings,
  onOverride,
  onClearOverride,
  overrideError = null,
}: FindingsPanelProps) {
  const groups = groupBySeverity(findings);

  return (
    <section data-testid="findings-panel">
      {overrideError && (
        <p data-testid="override-error" style={{ color: "#e06c75" }}>
          Override not saved (reverted): {overrideError}
        </p>
      )}
      {groups.length === 0 && (
        <p data-testid="findings-empty" style={{ color: "#98c379" }}>
          No findings — all rule checks passed.
        </p>
      )}
      {groups.map((group) => (
        <div key={group.severity} data-testid={`findings-${group.severity}`}>
          <h3
            style={{
              margin: "0.6rem 0 0.4rem",
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: SEVERITY_COLORS[group.severity],
            }}
          >
            {SEVERITY_LABELS[group.severity]} ({group.findings.length})
          </h3>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {group.findings.map((finding) => (
              <FindingRow
                key={finding.id}
                finding={finding}
                onOverride={onOverride}
                onClearOverride={onClearOverride}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
